// chainFieldBackfill.ts
import type { Editor } from '@tiptap/core';
import { CHAIN_FIELDS, isChainField } from './measurementChain';
import { CHAIN_EMPTY_MARKER, splitValueAndUnit } from './measurementValueParts';

/**
 * Mark the chained measurement values in a report that has no marks yet.
 *
 * Two situations need this, and neither is served by wrapping at populate time:
 *
 * 1. **Every already-saved report.** A saved report is stored as TipTap JSON,
 *    so `populateTemplate` never runs again on reopen — the placeholders were
 *    resolved to plain text once, months ago. Without a backfill the chain
 *    would only ever work on brand-new reports, which is not what was asked
 *    for.
 * 2. **After the clinician retypes a value in place.** Selecting a whole value
 *    and typing over it can take the mark with it. Re-running this (it is
 *    idempotent and cheap) re-marks whatever came loose, so a field cannot
 *    fall out of the chain by being edited.
 *
 * The anchor is the LABEL, which is part of the template and survives
 * everything: the measurement table renders `<span class="key">LVIDd:</span>
 * 4.8 cm` and the demographics header renders `<strong>HT: </strong>` in one
 * cell with the value in the next. Both come out of ProseMirror as a label
 * text node followed by a value text node, so one ordered walk handles both.
 */

/**
 * Report label → data key. Taken from the shipped clinic templates
 * (`Backend/v2/data/defaultClinicTemplates.js`), which is also where the
 * `{{key}}` placeholders come from — so the two cannot drift apart without the
 * template itself changing.
 *
 * Only labels whose value participates in the chain are listed. A label that
 * is not here is ignored entirely, which is what keeps "Patient ID:" or
 * "Date:" — both followed by digits — from ever being treated as a
 * measurement.
 */
const LABEL_TO_KEY: Record<string, string> = {
  // demographics header
  'ht': 'height',
  'wt': 'weight',
  'bsa': 'bsa',
  // left ventricle
  'lvidd': 'LVIDd',
  'lvids': 'LVIDs',
  'ivswd': 'IVSWd',
  'lvpwd': 'LVPWD',
  'lvmi': 'LVMI',
  'ef biplane': 'EF_Biplane',
  'lvedvi': 'LVEDVI',
  'lvesvi': 'LVESVI',
  'lvot diam': 'LVOT_Diam',
  'lvot vti': 'LVOT_VTI',
  'lvot vmax': 'LVOT_Vmax',
  // aortic valve
  'av vmax': 'AV_Vmax',
  'av pg': 'AV_PG',
  'av vti': 'AV_VTI',
  'ava vti': 'AVA_VTI',
  'ava vmax': 'AVA_Vmax',
  'dpi': 'DPI',
  // mitral valve / diastology
  'pht': 'PHT',
  'mva (pht)': 'MVA_PHT',
  'e vel': 'E_vel',
  'a vel': 'A_vel',
  'e/a': 'EA',
  "e' sept": 'E_sept',
  "e' lateral": 'E_lateral',
  "avg e/e'": 'Avg_EE',
  // right heart
  'tr vmax': 'TR_Vmax',
  'tr pg': 'TR_PG',
  'pv vmax': 'PV_Vmax',
  'pv pg': 'PV_PG',
  'rap': 'RAP',
  'rvsp': 'RVSP',
  // atria / aorta
  'la vol ind': 'LA_Vol_Ind',
  'ard': 'ARD',
  'ard index': 'ARD_Index',
  'asc aorta': 'Asc_Aorta',
  'asc aorta index': 'Asc_Aorta_Index',
};

const normalizeLabel = (text: string): string =>
  text
    .trim()
    .replace(/:\s*$/, '')
    .replace(/\s+/g, ' ')
    .toLowerCase();

/** Is this text node a label we recognise, and if so which key does it introduce? */
const keyForLabel = (text: string): string | null => {
  if (!text || !text.includes(':')) {
    // Header labels ("HT: ") and table labels ("LVIDd:") both carry the colon.
    // Requiring it stops a stray word matching a key by accident.
    return null;
  }
  const key = LABEL_TO_KEY[normalizeLabel(text)];
  return key && isChainField(key) ? key : null;
};

/**
 * The label a node STARTS with, whether or not its value shares the node.
 *
 * A cell reaches the document in either shape, decided by how the report happened to be
 * serialised: `"AV VTI:"` as its own node, or `"AV VTI: 20 cm"` as one. `keyForLabel`
 * only ever recognised the first, so the second read as an ordinary value — and a
 * field left blank would then adopt the NEXT field's whole cell as its own value.
 * Writing to it replaced the text, destroying that field's label and reading as the two
 * fields being wired to each other.
 *
 * Used to STOP the search, never to start one, so this only prevents a field from
 * reaching across into a cell that already belongs to something else.
 */
const labelPrefixKey = (text: string): string | null => {
  if (!text) {
    return null;
  }
  const colon = text.indexOf(':');
  if (colon === -1) {
    return null;
  }
  const key = LABEL_TO_KEY[normalizeLabel(text.slice(0, colon + 1))];
  return key && isChainField(key) ? key : null;
};

/**
 * Does this node begin a labelled cell of its OWN — any measurement, not just a chained
 * one?
 *
 * The stop rule cannot only know the fields we chain. A report is full of cells we do
 * not track ("RA Area:", "PVAccT:", "AV MG:"), and an unmeasured chained field would run
 * straight past them and adopt their value, then overwrite it on the first write.
 *
 * Deliberately narrow so prose is never mistaken for a label: a short leading token, the
 * kind a table cell uses, followed immediately by a colon. A sentence that happens to
 * contain a colon later on does not match, and neither does a printed value.
 */
const LABELLED_CELL = /^\s*[A-Za-z][A-Za-z0-9'’/()\s.-]{0,22}:/;

const startsAnotherCell = (text: string): boolean =>
  LABELLED_CELL.test(text) || labelPrefixKey(text) !== null;


/**
 * A value looks like a measurement if it has a digit in it — or if it is the placeholder
 * standing in for one.
 *
 * The placeholder must count. `stripChainMarks` removes the mark at save but leaves the
 * text, so on the next open an unmarked `--` sits where the value belongs. Read as "no
 * value", it earned a SECOND placeholder, and the cell printed `-- --`.
 *
 * It is matched on what the text STARTS with rather than what it equals, for the reports
 * already saved carrying `-- mmHg` — RVSP's placeholder used to be written with a unit,
 * and the chain copied it into the measurement cell. Compared against the bare marker
 * those cells read as empty, so every open added another placeholder: `RVSP: -- mmHg
 * -- mmHg`, one more on every view (Nikhil, 14 Sep). New reports no longer produce the
 * shape; these are the ones already out there, which no longer grow.
 *
 * Safe to read loosely because of which way it fails. It decides one thing — whether to
 * add a placeholder — so matching more means writing less, and no measurement can be
 * mistaken for one: a value is a number, and a negative has a single dash ("-2").
 */
const looksLikeValue = (text: string): boolean =>
  /\d/.test(text) || text.trim().startsWith(CHAIN_EMPTY_MARKER);

type TextHit = {
  pos: number;
  size: number;
  text: string;
  hasChainMark: boolean;
  /** Class of any non-chain span mark on this text — the prose anchors key off it. */
  spanClass: string | null;
};

/**
 * EF is printed in prose as well as in the measurement table, and prose has no
 * `Label:` cell to anchor on. Two anchors cover the whole fleet (checked against all 63
 * staging templates):
 *
 * - **Left Ventricle paragraph** — the value always follows the
 *   `<span class="lv-biplane">Simpson's Biplane</span>` span ("… method is 69 %."). 31 of
 *   63 templates carry that span, and it is part of the template rather than free prose,
 *   which makes it as reliable an anchor as `class="key"` is for the table.
 * - **Conclusions** — "EF 69 %." The `EF` token immediately before the rest of the
 *   sentence is specific enough; a bare number elsewhere in the sentence is never matched.
 *
 * Both capture everything up to the sentence's full stop, not just a "N %" shape —
 * this identity (`EF_Prose`, a separate key from the table's `EF_Biplane`; see
 * measurementChain.ts) is freeform on purpose, so "45-60%, mildly reduced" is as valid a
 * value here as "69 %" is. Anything looser than stopping at the anchor and the next full
 * stop was rejected deliberately: guessing at clinical prose beyond that risks writing
 * text into the wrong sentence.
 */
const PROSE_VALUE = /method is\s*([^.]*)\./i;
/**
 * The same two anchors with NO figure between them and the full stop.
 *
 * The prose passes find EF by matching the text already printed, so a report whose EF
 * never resolved reads "…method is ." and "EF ." and can never be filled: there is
 * nothing to mark. These place the marker where the figure belongs. (Both value regexes
 * above also match this shape, with an empty capture group — guarded against at the call
 * site so a blank sentence falls through to these instead of marking a zero-length range.)
 */
const PROSE_BLANK = /(method is\s*)(?=\.)/i;
const EF_BLANK_IN_CONCLUSION = /(\bEF\s*)(?=\.)/;
const EF_IN_CONCLUSION = /\bEF\b\s*([^.]*)\./;
const BIPLANE_ANCHOR_CLASS = 'lv-biplane';

/**
 * 28 of 63 staging templates still carry the single-brace "{efbp}" placeholder, which
 * never substitutes. The freeform capture above is loose enough to swallow it as if it
 * were a real value ("method is {efbp}." has something between "is" and "."), so it is
 * rejected explicitly rather than marked or treated as blank.
 */
const looksLikeUnresolvedPlaceholder = (text: string): boolean => /[{}]/.test(text);

/**
 * Add the `chainField` mark to any recognised measurement value that does not
 * already carry it. Returns how many were marked.
 *
 * Runs in a single transaction with `addToHistory` disabled, so a clinician's
 * first Ctrl+Z after opening a report undoes their own edit rather than an
 * invisible housekeeping step.
 */
export function backfillChainMarks(
  editor: Editor,
  options: { fillEmpty?: boolean } = {}
): number {
  if (!editor) {
    return 0;
  }
  // Off by default. Filling adds text to the document, so it is only ever done where the
  // caller has established the engine may write to this report at all — never on a
  // Finalized one, which must not gain characters just by being opened.
  const fillEmpty = options.fillEmpty === true;
  const { state } = editor;
  const chainMarkType = state.schema.marks.chainField;
  if (!chainMarkType) {
    return 0;
  }

  const hits: TextHit[] = [];
  state.doc.descendants((node, pos) => {
    if (!node.isText || !node.text) {
      return;
    }
    const classMark = node.marks.find(
      m => m.type.name !== 'chainField' && typeof m.attrs?.class === 'string'
    );
    hits.push({
      pos,
      size: node.nodeSize,
      text: node.text,
      hasChainMark: node.marks.some(m => m.type.name === 'chainField'),
      spanClass: (classMark?.attrs?.class as string) || null,
    });
  });

  const { tr } = state;
  let marked = 0;
  /** Places with no value, to be given a placeholder once the marking passes are done. */
  const empties: { key: string; at: number; space: boolean }[] = [];

  /** Mark `[from,to)` inside a text node as `key`, if it is not already marked. */
  const mark = (hit: TextHit, from: number, to: number, key: string) => {
    if (hit.hasChainMark || to <= from) {
      return;
    }
    tr.addMark(
      from,
      to,
      chainMarkType.create({
        class: key,
        title: CHAIN_FIELDS[key].label,
        'data-chain-warning': null,
      })
    );
    marked += 1;
  };

  for (let i = 0; i < hits.length - 1; i += 1) {
    const key = keyForLabel(hits[i].text);
    if (!key) {
      continue;
    }

    // The value is the next text in document order that actually carries
    // something. In the measurement table that is the rest of the same
    // paragraph; in the demographics header it is the next cell.
    //
    // Whitespace-only nodes are stepped over, and that step is what makes this
    // work on an already-saved report. A document parsed from HTML has its
    // adjacent unmarked text merged, so a label's value arrives as one node
    // (`" 4.4 cm"`). A document loaded from SAVED JSON keeps the split it was
    // serialised with, and every saved report splits the separator out:
    //
    //     {"text":"LVIDd:", marks:[span.key]}, {"text":" "}, {"text":"4.4 cm"}
    //
    // Treating that bare space as the value found no digit in it and marked
    // nothing — so on `/patientReports/:_id` the whole Measurement table was
    // invisible to the chain and a BSA edit moved none of the indexed values,
    // while the same edit on the studies page cascaded correctly. Nikhil,
    // 02 Sep: "here it is not updating on update of bsa".
    let value: TextHit | undefined;
    for (let j = i + 1; j < hits.length; j += 1) {
      const candidate = hits[j];
      // A label ends the search. An unmeasured field prints its label and no
      // value, and running on would mark the NEXT field's value against this
      // field's key — a wrong number in a clinical report, which is worse than
      // a field that simply does not participate.
      //
      // Matched as a PREFIX: the next cell may carry its label and value in one node.
      if (startsAnotherCell(candidate.text)) {
        break;
      }
      if (candidate.text.trim() === '') {
        continue; // separator, not the value
      }
      value = candidate;
      break;
    }
    if (value && value.hasChainMark) {
      continue;
    }
    if (!value || !looksLikeValue(value.text)) {
      // Nothing measured here. Remember the label so a placeholder can be put after it,
      // giving the field something to click and the engine somewhere to write.
      if (fillEmpty) {
        empties.push({
          key,
          at: hits[i].pos + hits[i].size,
          // The template writes "Label: value" — unless the label already ends in a space.
          space: !/\s$/.test(hits[i].text),
        });
      }
      continue;
    }

    // The template renders the value with a leading space
    // (`<span class="key">LVIDd:</span> 4.8 cm`). Marking the space too would
    // underline it, so the mark starts at the first non-space character.
    const leading = value.text.length - value.text.trimStart().length;
    const trailing = value.text.length - value.text.trimEnd().length;
    const from = value.pos + leading;
    let to = value.pos + value.text.length - trailing;
    if (to <= from) {
      continue;
    }

    // EF is printed "69 %" — the mark should cover the figure only, so typing
    // straight after the "%" lands outside it rather than being absorbed into
    // this occurrence and copied to every other one on the next chain update.
    if (CHAIN_FIELDS[key].unit === '%') {
      const trimmed = value.text.slice(leading, value.text.length - trailing);
      const { number } = splitValueAndUnit(trimmed);
      if (number) {
        to = from + number.length;
      }
    }

    mark(value, from, to, key);
  }

  // ── prose pass: EF printed in a sentence rather than a labelled cell ──────────────
  let efPlaceholder = false;
  for (let i = 0; i < hits.length; i += 1) {
    const hit = hits[i];
    if (hit.hasChainMark) {
      continue;
    }

    // Left Ventricle paragraph: the value follows the "Simpson's Biplane" span. A
    // SEPARATE identity from the table's EF_Biplane (Nikhil, 17 Sep) — freeform, so this
    // captures whatever the sentence says up to its full stop, not just a "N %" shape.
    const previous = i > 0 ? hits[i - 1] : null;
    if (previous && previous.spanClass === BIPLANE_ANCHOR_CLASS) {
      const found = PROSE_VALUE.exec(hit.text);
      if (found && found[1] && !looksLikeUnresolvedPlaceholder(found[1])) {
        const valueIndex = hit.text.indexOf(found[1], found.index);
        if (valueIndex !== -1) {
          mark(hit, hit.pos + valueIndex, hit.pos + valueIndex + found[1].length, 'EF_Prose');
          continue;
        }
      }
      const blank = fillEmpty && !efPlaceholder ? PROSE_BLANK.exec(hit.text) : null;
      if (blank) {
        empties.push({
          key: 'EF_Prose',
          at: hit.pos + blank.index + blank[0].length,
          space: false,
        });
        efPlaceholder = true;
        continue;
      }
    }

    // Conclusions: "… systolic function. EF 69 %." Same freeform capture, up to the
    // full stop — "EF 45-60%, mildly reduced." is as valid a sentence as "EF 69 %."
    const inConclusion = EF_IN_CONCLUSION.exec(hit.text);
    if (inConclusion && inConclusion[1] && !looksLikeUnresolvedPlaceholder(inConclusion[1])) {
      const valueIndex = hit.text.indexOf(inConclusion[1], inConclusion.index);
      if (valueIndex !== -1) {
        mark(hit, hit.pos + valueIndex, hit.pos + valueIndex + inConclusion[1].length, 'EF_Prose');
      }
      continue;
    }
    const blankConclusion = fillEmpty ? EF_BLANK_IN_CONCLUSION.exec(hit.text) : null;
    if (blankConclusion) {
      empties.push({
        key: 'EF_Prose',
        at: hit.pos + blankConclusion.index + blankConclusion[0].length,
        space: false,
      });
    }
  }

  // ── placeholder pass: give an unmeasured field something to click ────────────────
  //
  // Runs LAST and back-to-front. Adding text shifts every position after it, and the
  // marking passes above hold positions from the untouched document — `addMark` does not
  // change the document's length, so those stay valid, but an insertion would invalidate
  // any insertion recorded before it.
  // Sorted because the label pass and the prose passes collect in different document
  // orders; inserting out of order would shift a position recorded earlier.
  empties.sort((a, b) => a.at - b.at);
  for (let i = empties.length - 1; i >= 0; i -= 1) {
    const slot = empties[i];
    // The template writes "Label: value", so the placeholder needs the same separator
    // unless the label already carries its own trailing space.
    const marker = state.schema.text(CHAIN_EMPTY_MARKER, [
      chainMarkType.create({
        class: slot.key,
        title: CHAIN_FIELDS[slot.key].label,
        'data-chain-warning': null,
      }),
    ]);
    // The separator is inserted UNMARKED, for the same reason the marking pass starts at
    // the first non-space character: a marked space renders as an underlined gap.
    const nodes = [marker];
    // EF's mark is meant to cover the figure only, never the unit (see chainFieldMarkup.ts).
    // A placeholder is where that value first comes into existence, so the static "%" has
    // to be planted here too — otherwise the first figure ever typed has nowhere to land a
    // unit beside it, and `applyChainUpdates` writes it back into the mark instead.
    const fieldUnit = CHAIN_FIELDS[slot.key].unit;
    if (fieldUnit === '%') {
      nodes.push(state.schema.text(` ${fieldUnit}`));
    }
    tr.insert(slot.at, slot.space ? [state.schema.text(' '), ...nodes] : nodes);
    marked += 1;
  }

  if (marked > 0) {
    tr.setMeta('addToHistory', false);
    editor.view.dispatch(tr);
  }

  return marked;
}
